import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { ExtraMessageWithBlurredBg } from '@sorare/core/src/components/onboarding/ExtraMessageWithBlurredBg';
import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useOnboardingHighlights } from '@sorare/core/src/hooks/useOnboardingHighlights';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useNBAEvents } from '@sorare/marketplace/src/shared/events/lib';

import { GalleryCardExtra } from './GalleryCardExtra';

const StyledButton = styled(Button)`
  z-index: 2;
  position: absolute;
  bottom: var(--quadruple-unit);
  box-shadow: 0px 0px var(--double-unit) var(--c-black);
`;

export const GalleryOnboarding = () => {
  const track = useNBAEvents();
  const { currentUser, blockchainCardsCount } = useCurrentUserContext();
  const onboardingHighlights = useOnboardingHighlights();
  const { lifecycle, update: updateLifecycle } = useLifecycle();

  const hideOnboarding =
    !onboardingHighlights || lifecycle?.sawGalleryOnboarding;

  const [step, setStep] = useState(0);
  const isTabletAndAbove = useIsTabletAndAbove();

  const hasCommonCards = currentUser?.cardCounts?.common ?? 0;

  const shouldNotRender = !hasCommonCards || hideOnboarding;

  useEffect(() => {
    if (!shouldNotRender) {
      track('View Gallery Onboarding');
    }
  }, [track, shouldNotRender]);

  if (!blockchainCardsCount || shouldNotRender) return null;

  if (blockchainCardsCount > 0 && step !== 2) {
    setStep(2);
  }
  if (step === 0) {
    return (
      <HighlightOverlay
        key="ManagerProfileGallery"
        disableBackdropClose
        onClose={() => setStep(1)}
        highlightNames={['ManagerProfileGallery']}
        extras={[
          {
            nextTo: 'ManagerProfileGallery',
            render: ({ top, left, right, bottom }) => (
              <>
                <ExtraMessageWithBlurredBg
                  style={{
                    position: 'absolute',
                    top: `calc(${top}px - var(--unit) * 9)`,
                    width: isTabletAndAbove ? '340px' : right - left,
                    left: isTabletAndAbove
                      ? `calc(${left}px + (${right - left}px - 340px) / 2)`
                      : left,
                  }}
                >
                  <LabelL>
                    <FormattedMessage
                      id="NBA.managerProfile.GalleryOnboarding"
                      defaultMessage="You can find the Cards you own here"
                    />
                  </LabelL>
                </ExtraMessageWithBlurredBg>
                <StyledButton
                  size="large"
                  color="secondary"
                  onClick={() => setStep(1)}
                  style={{
                    top: isTabletAndAbove
                      ? `calc(${bottom}px + var(--double-unit))`
                      : '',
                    width: isTabletAndAbove ? '340px' : right - left,
                    left: isTabletAndAbove
                      ? `calc(${left}px + (${right - left}px - 340px) / 2)`
                      : left,
                  }}
                >
                  <FormattedMessage {...glossary.next} />
                </StyledButton>
              </>
            ),
          },
        ]}
      />
    );
  }

  if (step === 1) {
    return (
      <HighlightOverlay
        key="ManagerProfileGalleryCard"
        disableBackdropClose
        onClose={() => {
          setStep(2);
        }}
        highlightNames={['ManagerProfileGalleryCard']}
        extras={[
          {
            nextTo: 'ManagerProfileGalleryCard',
            render: props => <GalleryCardExtra {...props} />,
          },
        ]}
      />
    );
  }

  return (
    <HighlightOverlay
      onClose={() => {
        updateLifecycle(LIFECYCLE.sawGalleryOnboarding, true);
      }}
      extras={[
        {
          nextTo: 'MyCardsCommonMarket',
          render: props => (
            <ExtraMessageWithBlurredBg
              align="center"
              anchor="top"
              positionProps={props}
              fullWidth={!isTabletAndAbove}
            >
              <LabelL>
                <FormattedMessage
                  id="CommonMarket.onboarding.title"
                  defaultMessage="Common Cards are free! You can swap yours here when you’re ready"
                />
              </LabelL>
            </ExtraMessageWithBlurredBg>
          ),
        },
      ]}
      highlightNames={['MyCardsCommonMarket']}
    />
  );
};
